/**
 * Shared enums for EduBorg.
 *
 * Roles use the priority numbering from the old build:
 *   super_admin (7) > admin (6) > department_admin (5) > instructor (4) > teaching_assistant (3) > student (2) > observer (1)
 */

export const APP_ROLES = [
  'super_admin',
  'admin',
  'department_admin',
  'instructor',
  'teaching_assistant',
  'student',
  'observer',
] as const;
export type AppRole = (typeof APP_ROLES)[number];

export const ROLE_PRIORITY: Record<AppRole, number> = {
  super_admin: 7,
  admin: 6,
  department_admin: 5,
  instructor: 4,
  teaching_assistant: 3,
  student: 2,
  observer: 1,
};

export const ACCOUNT_TYPES = ['school', 'instructor', 'student'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const ORG_TYPES = ['school', 'individual', 'department'] as const;
export type OrgType = (typeof ORG_TYPES)[number];

export const COURSE_STATUSES = ['draft', 'active', 'archived', 'deleted'] as const;
export type CourseStatus = (typeof COURSE_STATUSES)[number];

export const MODULE_ITEM_TYPES = [
  'lesson',
  'assignment',
  'assessment',
  'knowledge_check',
  'discussion',
  'file',
  'link',
  'internal_link',
] as const;
export type ModuleItemType = (typeof MODULE_ITEM_TYPES)[number];

/** Where an assignment can be exported. Studyborg integration is post-v1; field included now to avoid retrofit. */
export const ASSIGNMENT_TARGET_PLATFORMS = ['internal', 'studyborg', 'other'] as const;
export type AssignmentTargetPlatform = (typeof ASSIGNMENT_TARGET_PLATFORMS)[number];

/**
 * Block types that can appear inside a lesson's body. Matches the old build's
 * BlockEditor surface. Concrete payloads live in `content_blocks.payload_json`;
 * the editor narrows by `type`.
 */
export const CONTENT_BLOCK_TYPES = [
  'text',
  'heading',
  'image',
  'video',
  'embed',
  'file',
  'document',
  'freehand',
] as const;
export type ContentBlockType = (typeof CONTENT_BLOCK_TYPES)[number];

/**
 * Per-course role on top of the platform-wide `app_role`. A user can be an
 * `instructor` at the org level AND a `student` in another instructor's
 * course — that's modelled here, not in `user_roles`.
 */
export const ENROLLMENT_ROLES = ['student', 'teaching_assistant', 'observer'] as const;
export type EnrollmentRole = (typeof ENROLLMENT_ROLES)[number];

/**
 * Question types for assessments. The 15-type set in the master plan, modelled
 * after the old build's coverage:
 *
 *   multiple_choice  one correct, single radio
 *   multiple_select  many correct, checkbox
 *   true_false       binary
 *   short_answer     text, exact-match grading (case/whitespace tolerance)
 *   long_answer      essay; AI suggests feedback, instructor scores
 *   numeric          number, with tolerance
 *   fill_in_blank    one-or-many blanks inside prose
 *   matching         left↔right pair-up
 *   ordering         drag-arrange a list
 *   hotspot          click an image at the right spot
 *   file_upload      submit a file (R2-dependent)
 *   code             code editor with stdout-compare grading
 *   formula          math expression with symbolic equality
 *   classification   bucket items into named groups
 *   drag_drop        free-form drag targets
 */
export const QUESTION_TYPES = [
  'multiple_choice',
  'multiple_select',
  'true_false',
  'short_answer',
  'long_answer',
  'numeric',
  'fill_in_blank',
  'matching',
  'ordering',
  'hotspot',
  'file_upload',
  'code',
  'formula',
  'classification',
  'drag_drop',
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

/**
 * Where an attempt is in its lifecycle.
 *   in_progress    student is still taking it
 *   submitted      student clicked Submit (or hit time limit)
 *   auto_submitted server submitted on their behalf (offline / connection lost)
 *   graded         scoring complete (deterministic + any AI suggestions)
 *   released       grade is visible to the student
 */
/**
 * Conversation kind. Drives both UX (different headers for direct vs. group)
 * and the realtime channel naming (`messages:conversation_id={id}` regardless;
 * the kind narrows render).
 */
export const CONVERSATION_KINDS = ['direct', 'group', 'course_room'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

/**
 * What a notification is *about*. Matches old build's broad categories so
 * the bell-icon dropdown can group + filter without a refactor later.
 */
export const NOTIFICATION_KINDS = [
  'message',
  'mention',
  'assignment_due',
  'assignment_graded',
  'discussion_reply',
  'announcement',
  'risk_flag',
  'milestone',
  'streak',
  'system',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * Where an announcement reaches. Cross-course is the master plan's "post once,
 * pick targets" feature with single-notification dedup.
 */
export const ANNOUNCEMENT_SCOPES = ['course', 'org', 'cross_course', 'platform'] as const;
export type AnnouncementScope = (typeof ANNOUNCEMENT_SCOPES)[number];

export const ATTEMPT_STATUSES = [
  'in_progress',
  'submitted',
  'auto_submitted',
  'graded',
  'released',
] as const;
export type AttemptStatus = (typeof ATTEMPT_STATUSES)[number];
